import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import makeAnimated from "react-select/animated";
import api from "../../../adapters/axios";
import {
FaCheck,
FaExternalLinkAlt,
FaCog,
FaChevronUp,
FaChevronDown,
} from "react-icons/fa";
import * as XLSX from "xlsx";
import "../AlertMenu/AlertMenu.css";
import "./DragDrop.css";
import { format, parseISO } from "date-fns";
import DatePicker from "react-datepicker";

type TagOpt = {
value: string;
label: string;
origin: "wendwise" | "user" | "new";
};
type SavedItem = {
id: string;
tags: {
origin: any; id: number | string; tagName: string 
}[]; // backend returns id and tagName
blurb: string;
url: string;
source: string;
date: string;
addedBy: string;
user_id:number;
user_tag_ids?: number[];
company_tag_ids?: number[];
};

const formatDate = (isoDate: string) => {
if (!isoDate) return "";
return new Date(isoDate).toLocaleDateString("en-US", {
year: "2-digit",
month: "2-digit",
day: "2-digit",
});
};

const animatedComponents = makeAnimated();
const BLOCKED_EXTENSIONS = [
".exe",
".js",
".jar",
".sh",
".jsp",
".jse",
".msi",
".bat",
".cmd",
];
const URL_PROTOCOL_WHITELIST = ["http:", "https:"];

function isValidHttpUrl(text: string): boolean {
try {
const url = new URL(text.trim());
if (!URL_PROTOCOL_WHITELIST.includes(url.protocol)) return false;
const lower = url.pathname.toLowerCase();
return !BLOCKED_EXTENSIONS.some((ext) => lower.endsWith(ext));
} catch {
return false;
}
}

// Custom styles for react-select to maintain consistent width
const selectStyles = {
control: (provided: any) => ({
...provided,
minWidth: '250px',
width: '100%',
}),
menu: (provided: any) => ({
...provided,
minWidth: '250px',
width: '100%',
}),
menuList: (provided: any) => ({
...provided,
minWidth: '250px',
}),
};

export default function DragDrop() {
const navigate = useNavigate();
const currentUserId = Number(localStorage.getItem("userId"));
// Collapse state
const [isCollapsed, setIsCollapsed] = useState(false);

// Form states
const [url, setUrl] = useState("");
const [tags, setTags] = useState<TagOpt[]>([]); // combined selected tags (user + wendwise + new)
const [newTag, setNewTag] = useState("");
const [blurb, setBlurb] = useState("");
const [saving, setSaving] = useState(false);
const [message, setMessage] = useState("");
const [isTagModalOpen, setIsTagModalOpen] = useState(false);
const [tempTag, setTempTag] = useState("");
const [wendwiseOptions, setWendwiseOptions] = useState<TagOpt[]>([]);
const [userTagOptions, setUserTagOptions] = useState<TagOpt[]>([]);
const [errors, setErrors] = useState<{ url?: string; tag?: string }>({});
const [flashMessage, setFlashMessage] = useState("");
const [fadeOut, setFadeOut] = useState(false);
const [page, setPage] = useState(1);
const [pageSize, setPageSize] = useState(10);
const [totalItems, setTotalItems] = useState(0);
const showFlash = (msg: string) => {
setFlashMessage(msg);
setFadeOut(false);
setTimeout(() => setFadeOut(true), 1500); // start fade after 1.5s
setTimeout(() => setFlashMessage(""), 2000); // remove after fade
};

const today = useMemo(() => new Date().toISOString().slice(0, 10), []);

// List states
const [items, setItems] = useState<SavedItem[]>([]);
const [loadingItems, setLoadingItems] = useState(false);

// Filters
const [searchBlurb, setSearchBlurb] = useState("");
const [filterTags, setFilterTags] = useState<TagOpt[]>([]);
const [fromDate, setFromDate] = useState("");
const [toDate, setToDate] = useState("");

// Selection
const [selectedItems, setSelectedItems] = useState<string[]>([]);
const [selectAll, setSelectAll] = useState(false);

// Inline edit states
const [editingItemId, setEditingItemId] = useState<string | null>(null);
const [editTag, setEditTag] = useState<TagOpt[] | null>(null); // multiple selection for inline tag edit
const [editBlurb, setEditBlurb] = useState("");
const [editingField, setEditingField] = useState<{
itemId: string;
field: "tag" | "blurb";
} | null>(null);
const [usedTagOptions, setUsedTagOptions] = useState<TagOpt[]>([]);

// Load tags
const loadUsedTagsForFilter = async () => {
try {
const res = await api.get("/used-tags");
const used: TagOpt[] = [
...(res.data?.companyTags || []).map((t: any) => ({
value: String(t.id),
label: t.tagName,
origin: "wendwise",
})),
...(res.data?.userTags || []).map((t: any) => ({
value: String(t.id),
label: t.tagName,
origin: "user",
})),
];
setUsedTagOptions(used);
} catch (e) {
console.error("Failed to load used filter tags", e);
}
};
useEffect(() => {
loadUsedTagsForFilter();
}, []);
useEffect(() => {
async function loadTags() {
try {
const [wwRes, userRes] = await Promise.all([
api.get("/tags").catch(() => ({ data: [] })), // wendwise tags endpoint (your original code used /tags)
api.get("/user-tags").catch(() => ({ data: [] })),
]);
const ww: TagOpt[] = (wwRes.data || []).map((t: any) => ({
value: String(t.id ?? t.value ?? t.tagName),
label: t.tagName ?? t.label ?? t.name,
origin: "wendwise",
}));
const ut: TagOpt[] = (userRes.data || []).map((t: any) => ({
value: String(t.id ?? t.value ?? t.tagName),
label: t.tagName ?? t.label ?? t.name,
origin: "user",
}));
setWendwiseOptions(ww);
setUserTagOptions(ut);
} catch (err) {
console.error(err);
}
}
loadTags();
}, []);

// Validation - allow multiple tags (>=1)
const validateForm = () => {
const newErrors: { url?: string; tag?: string } = {};
if (!url.trim()) {
newErrors.url = "URL is required.";
} else if (!isValidHttpUrl(url)) {
newErrors.url = "Please enter a valid http/https URL.";
}
if (!tags.length) {
newErrors.tag = "Please select at least one tag.";
}
setErrors(newErrors);
return Object.keys(newErrors).length === 0;
};

useEffect(() => {
if (url.trim() && isValidHttpUrl(url)) setErrors((prev) => ({ ...prev, url: undefined }));
}, [url]);
useEffect(() => {
if (tags.length > 0) setErrors((prev) => ({ ...prev, tag: undefined }));
}, [tags]);

// Fetch items
const fetchItems = async () => {
setLoadingItems(true);
try {
const params: any = {
page,
pageSize,
};
if (filterTags.length) {
params.tags = filterTags
.filter(t => t.origin === "user" || t.origin === "wendwise") // important
.map(t => `${t.origin}:${Number(t.value)}`)
.join(",");
} 
if (searchBlurb.trim()) params.blurb = searchBlurb.trim();
if (fromDate) params.from = fromDate;
if (toDate) params.to = toDate;
console.log("Fetching items with params:", params);
const res = await api.get("/dragdrop/items", { params });
const fetchedItems = res.data?.items || res.data || [];
setItems(fetchedItems);
setTotalItems(res.data?.total ?? fetchedItems.length); // if backend sends total
} catch (err) {
console.error("Error fetching items:", err);
} finally {
setLoadingItems(false);
}
};
// Re-fetch whenever filters or page change
useEffect(() => {
const delayDebounce = setTimeout(() => fetchItems(), 300);
return () => clearTimeout(delayDebounce);
}, [searchBlurb, filterTags, fromDate, toDate, page,pageSize]);

// Drag & Paste
const onDrop = useCallback((e: React.DragEvent) => {
e.preventDefault();
e.stopPropagation();
if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
setMessage("Files are not allowed. Drop a URL.");
e.dataTransfer.clearData();
return;
}
const droppedUrl = e.dataTransfer.getData("text/uri-list") || e.dataTransfer.getData("text/plain");
if (isValidHttpUrl(droppedUrl)) {
setUrl(droppedUrl.trim());
setMessage("");
} else {
setMessage("Only valid http/https URLs are allowed.");
}
}, []);

const onPaste = useCallback((e: React.ClipboardEvent<HTMLInputElement>) => {
const text = e.clipboardData.getData("text");
if (isValidHttpUrl(text)) {
setUrl(text.trim());
setMessage("");
}
}, []);

const allowDrop = (e: React.DragEvent) => e.preventDefault();

// Inline tag creation (inline input + "addInlineTag" used nowhere in UI - kept for compatibility)
const addInlineTag = () => {
const raw = (newTag || "").trim();
if (!raw) return;
if (!/^[\w\- ]{1,60}$/.test(raw)) {
setMessage("Invalid tag format.");
return;
}
const exists = [...wendwiseOptions, ...userTagOptions, ...tags].some((t) => t.label.toLowerCase() === raw.toLowerCase());
if (exists) {
setMessage("Tag already exists/selected.");
return;
}
const opt: TagOpt = { value: `new:${raw}`, label: raw, origin: "new" };
setTags((prev) => [...prev, opt]);
setNewTag("");
setMessage("");
};

const canSave = url && tags.length > 0 && isValidHttpUrl(url) && blurb.length <= 1000;

// Save new item (single API request with arrays)
const onSave = async () => {
if (!validateForm()) return;
setSaving(true);
setMessage("");
try {
// Separate new tags
const newTags = tags.filter((t) => t.origin === "new").map((t) => t.label);
let createdIds: { id: number; label: string; origin: "user" | "wendwise" }[] = [];
if (newTags.length) {
try {
const res = await api.post("/user-tags/bulk", { tags: newTags });
createdIds = (res.data?.ids || []).map((id: any, idx: number) => ({
id: Number(id),
label: newTags[idx],
origin: "user",
}));
} catch {
for (const name of newTags) {
const r = await api.post("/user-tags", { tagName: name });
createdIds.push({ id: Number(r.data?.id), label: name, origin: "user" });
}
}
}
// Determine source
let source = "";
try {
const urlObj = new URL(url);
source = urlObj.hostname.replace(/^www\./, "");
} catch {}
// Combine tags
const allUserTagIds = [
...tags.filter((t) => t.origin === "user").map((t) => Number(t.value)),
...createdIds.filter((t) => t.origin === "user").map((t) => t.id),
];
const allCompanyTagIds = [
...tags.filter((t) => t.origin === "wendwise").map((t) => Number(t.value)),
...createdIds.filter((t) => t.origin === "wendwise").map((t) => t.id),
];
const payload = {
blurb: blurb.trim(),
url,
source,
user_tag_ids: allUserTagIds,
company_tag_ids: allCompanyTagIds,
allUserTags: allUserTagIds,
allCompanyTags: allCompanyTagIds,
};
await api.post("/items", payload);
// Clear form first
setUrl("");
setTags([]);
setBlurb("");
// Refresh tag options in case new user tags were created
// const userRes = await api.get("/user-tags").catch(() => ({ data: [] }));
// const updatedUserTags: TagOpt[] = (userRes.data || []).map((t: any) => ({
// value: String(t.id ?? t.value ?? t.tagName),
// label: t.tagName ?? t.label ?? t.name,
// origin: "user",
// }));
// setUserTagOptions(updatedUserTags);
await Promise.all([fetchItems(), loadUsedTagsForFilter()]);
showFlash("Saved!"); // <-- flash now only after fetchItems completes
} catch (err) {
console.error(err);
setFlashMessage("Failed to save.");
} finally {
setSaving(false);
}
};
// Selection
const toggleSelectAll = () => {
if (selectAll) setSelectedItems([]);
else setSelectedItems(items.map((i) => i.id));
setSelectAll(!selectAll);
};
const toggleSelectOne = (id: string) => {
setSelectedItems((prev) => (prev.includes(id) ? prev.filter((sid) => sid !== id) : [...prev, id]));
};


// Export Excel
const exportToExcel = () => {
const dataToExport = selectedItems.length
? items.filter((i) => selectedItems.includes(i.id))
: items; 
const worksheet = XLSX.utils.json_to_sheet(
dataToExport.map((i) => ({
Tags: i.tags.map((t) => t.tagName).join(", "),
Blurb: i.blurb,
Link: i.url,
Source: i.source,
"Added By": i.addedBy,
}))
);
const workbook = XLSX.utils.book_new();
XLSX.utils.book_append_sheet(workbook, worksheet, "Saved Items");
XLSX.writeFile(workbook, "saved_items.xlsx");
};

// Delete selected
const deleteSelected = async () => {
const currentUserId = Number(localStorage.getItem("userId"));
console.log('currentuser',currentUserId)
// pick selected items or all filtered items
const idsToDelete = (selectedItems.length
? selectedItems
: items.map((i) => i.id)) 
.filter((id) => {
const item = items.find((i) => i.id === id);
return item?.user_id === currentUserId;
});
if (!idsToDelete.length) {
alert("You can only delete items you added.");
return;
}
if (!window.confirm("Are you sure you want to delete selected/filtered items?")) return;
try {
await api.delete("/dragdrop/items/bulk", { data: { ids: idsToDelete } });
setSelectedItems([]);
setSelectAll(false);
fetchItems();
} catch (err) {
console.error(err);
}
};

// Navigate
const handleManageUserTags = () => {
navigate("/user-tags");
};

// FIXED: Inline tag edit - properly map both wendwise and user tags
const openTagEditorForItem = (item: SavedItem) => {
console.log("Opening tag editor for item:", item);
console.log("Item tags:", item.tags);
console.log("Available wendwise options:", wendwiseOptions);
console.log("Available user options:", userTagOptions);
const allOptions = [...wendwiseOptions, ...userTagOptions];
const found: TagOpt[] = [];
// Map each tag from the item to the corresponding option
(item.tags || []).forEach((itemTag) => {
const tagId = String(itemTag.id);
const origin = itemTag.origin; // comes directly from backend now
const matchedOption =
(origin === "user"
? userTagOptions.find((opt) => String(opt.value) === tagId)
: wendwiseOptions.find((opt) => String(opt.value) === tagId)) ||
{
value: tagId,
label: itemTag.tagName,
origin,
};
found.push(matchedOption);
});
console.log("Mapped tags for editing:", found);
setEditTag(found);
setEditingField({ itemId: item.id, field: "tag" });
};

// FIXED: Inline update tags for an item (send arrays)
const saveInlineTags = async (itemId: string) => {
if (!editTag) return;
try {
// Extract user tags
const allUserTags = editTag
.filter((t) => t.origin === "user")
.map((t) => Number(t.value))
.filter((id) => !isNaN(id)); // Filter out invalid IDs
// Extract company tags
const allCompanyTags = editTag
.filter((t) => t.origin === "wendwise")
.map((t) => Number(t.value))
.filter((id) => !isNaN(id)); // Filter out invalid IDs
console.log("Updating item:", itemId, { allUserTags, allCompanyTags });
console.log("Edit tag data:", editTag);
await api.post(`/dragdrop/items/${itemId}/update`, {
allUserTags,
allCompanyTags,
});
setEditingField(null);
setEditTag(null);
fetchItems();
} catch (err) {
console.error("Failed to update tags", err);
}
};

// Inline update blurb
const saveInlineBlurb = async (itemId: string) => {
try {
await api.post(`/dragdrop/items/${itemId}/update`, { blurb: editBlurb });
setEditingField(null);
fetchItems();
} catch (err) {
console.error("Failed to update blurb", err);
}
};

return (
<div className="alert-page-wrapper dragdrop-page">
{/* Flash message */}
{/* {flashMessage && <div className={`status-msg-flash ${fadeOut ? "fade-out" : ""}`}>{flashMessage}</div>} */}

{/* Toggle Collapse */}
<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px" }}>
<h2>
{" "}
<span className="alert-heading">Drag &amp; Drop</span>
</h2>
<div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
<button className="btn btn-secondary" onClick={() => setIsCollapsed((prev) => !prev)} style={{ display: "flex", alignItems: "center", gap: "4px" }}>
{isCollapsed ? (
<>
<FaChevronDown /> Expand
</>
) : (
<>
<FaChevronUp /> Minimize
</>
)}
</button>
<button className="btn btn-secondary" onClick={handleManageUserTags} style={{ display: "flex", alignItems: "center", gap: "4px" }}>
Manage User Tags
</button>
</div>
</div>

{!isCollapsed && (
<>
{/* FORM */}
<div className="drop-zone" onDrop={onDrop} onDragOver={allowDrop}>
{url ? (
<div className="url-preview">
<span className="label">URL</span>
<input type="text" value={url} onChange={(e) => setUrl(e.target.value)} onPaste={onPaste} />
</div>
) : (
<div className="drop-instructions">
<p>Drag a link or URL here (or click to paste)</p>
<input className="hidden-url-input" value={url} placeholder="Paste URL here" onPaste={onPaste} onChange={(e) => setUrl(e.target.value)} />
</div>
)}
</div>
{errors.url && <div className="error-text">{errors.url}</div>}

{/* Tags */}
<label className="row-label">Tag (choose one)</label>
<div className="tags-controls">
<Select
placeholder="Wendwise tags…"
options={wendwiseOptions}
components={animatedComponents}
value={tags.filter((t) => t.origin === "wendwise")}
onChange={(opts) => {
const selected = (opts as TagOpt[]) || [];
const others = tags.filter((t) => t.origin !== "wendwise");
setTags([...others, ...selected]);
}}
isClearable
isMulti
styles={selectStyles}
/>

<Select
placeholder="Your tags…"
options={userTagOptions}
components={animatedComponents}
value={tags.filter((t) => t.origin === "user")}
onChange={(opts) => {
const selected = (opts as TagOpt[]) || [];
const others = tags.filter((t) => t.origin !== "user");
setTags([...others, ...selected]);
}}
isClearable
isMulti
styles={selectStyles}
/>

<div className="new-tag">
<button
className="btn btn-primary"
onClick={() => setIsTagModalOpen(true)}
>
+ Create New Tag
</button>
</div>
</div>

{/* Modal & Blurb */}
{isTagModalOpen && (
<div className="custom-modal-overlay" onClick={() => setIsTagModalOpen(false)}>
<div className="custom-modal" onClick={(e) => e.stopPropagation()}>
<div className="tag-modal-header">
<h3>Create New Tag</h3>
<button className="close-btn" onClick={() => setIsTagModalOpen(false)}>
×
</button>
</div>
<p className="tag-rules">
Allowed characters: space, hyphen, underscore <br /> Maximum: 60 characters
</p>
<div className="create-tag-input">
<input type="text" value={tempTag} onChange={(e) => setTempTag(e.target.value)} maxLength={60} placeholder="Enter Tag" />
<button
className="btn btn-primary"
onClick={async () => {
if (!tempTag.trim()) return;
try {
const res = await api.post("/user-tags/bulk", { tags: [tempTag.trim()] });
const newId = res.data?.ids?.[0]?.toString() || tempTag.trim();
const newTagOpt: TagOpt = { value: newId, label: tempTag.trim(), origin: "user" };
setUserTagOptions((prev) => [...prev, newTagOpt]);
setTags((prev) => [...prev, newTagOpt]);
setTempTag("");
setIsTagModalOpen(false);
} catch {
alert("Failed to create tag.");
}
}}
>
Create Tag
</button>
</div>
</div>
</div>
)}
<label className="row-label">Blurb / Notes (optional)</label>
<textarea className="blurb" placeholder="Drop or type up to 1000 characters…" value={blurb} maxLength={1000} onChange={(e) => setBlurb(e.target.value)} />
<div className="char-count">{blurb.length}/1000</div>
<label className="row-label">Date</label>
<input type="date" value={today} readOnly />
<div className="actions">
{flashMessage && <div className={`status-msg-flash ${fadeOut ? "fade-out" : ""}`}>{flashMessage}</div>}
<button className="btn-primary save-btn" onClick={onSave} disabled={saving || !canSave}>
{saving ? "Saving…" : "Save"}
</button>

{message && <span className="status-msg">{message}</span>}
</div>
</>
)}

{/* LISTING */}
<div
style={{
display: "flex",
alignItems: "center",
gap: "10px",
marginTop: "6px",
fontSize: "0.9rem",
}}
>
</div>

<hr style={{ margin: "20px 0" }} />
<h3>Saved Items</h3>
<div className="filters-row">
<Select
isMulti
placeholder="Filter by tags"
options={usedTagOptions}
value={filterTags}
onChange={(v) => setFilterTags(v as TagOpt[])}
/>

<input type="text" className="filter-blurb" placeholder="Search Blurb" value={searchBlurb} onChange={(e) => setSearchBlurb(e.target.value)} />
<div className="date-filters">
<DatePicker selected={fromDate ? new Date(fromDate) : null} onChange={(date: Date | null) => { if (date) setFromDate(date.toISOString().slice(0, 10)); else setFromDate(""); }} dateFormat="MM/dd/yyyy" placeholderText="MM/DD/YYYY" />
<DatePicker selected={toDate ? new Date(toDate) : null} onChange={(date: Date | null) => { if (date) setToDate(date.toISOString().slice(0, 10)); else setToDate(""); }} dateFormat="MM/dd/yyyy" placeholderText="MM/DD/YYYY" />
</div>
<button className="btn btn-primary" onClick={exportToExcel}>
Export Excel
</button>
<button className="btn btn-danger" onClick={deleteSelected}>
Delete
</button>
</div>
{/* Items summary row */}
<div className="items-summary-row">
<div className="items-count">
<span className="count-badge total">
Total Items: {totalItems}
</span>

{selectedItems.length > 0 && (
<span className="count-badge selected">
{selectedItems.length} selected
</span>
)}
</div>
</div>

<div className="items-table-wrapper" style={{ position: "relative" }}>
{editingField?.field === "tag" && (
<div className="overlay-blur" onClick={() => setEditingField(null)} style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", backdropFilter: "blur(3px)", backgroundColor: "rgba(0,0,0,0.1)", zIndex: 999 }} />
)}
<table className="items-table">
<thead>
<tr>
<th>
<input type="checkbox" checked={selectAll} onChange={toggleSelectAll} />
</th>
<th>Tag</th>
<th>Blurb</th>
<th>Link</th>
<th>Source</th>
<th>Added By</th>
<th>Date</th>
</tr>
</thead>
<tbody>
{loadingItems ? (
<tr>
<td colSpan={7}>Loading…</td>
</tr>
) : items.length ? (
items.map((item) => (

<tr key={item.id}>
<td>
<input type="checkbox" checked={selectedItems.includes(item.id)} onChange={() => toggleSelectOne(item.id)} />
</td>
<td>
{item.user_id === Number(localStorage.getItem("userId")) ? (
editingField?.itemId === item.id && editingField.field === "tag" ? (
<div
className="inline-edit-container"
style={{
display: "flex",
alignItems: "center",
gap: "8px",
minWidth: "250px",
position: "relative",
zIndex: 1000,
}}
>
<Select
isMulti
value={editTag ?? []}
onChange={(opts) => setEditTag(opts as TagOpt[])}
options={[...wendwiseOptions, ...userTagOptions]}
isClearable
menuPortalTarget={document.body}
styles={{
menuPortal: (base) => ({ ...base, zIndex: 9999 }),
control: (provided) => ({ ...provided, minHeight: "40px", minWidth: "250px" }),
menu: (provided) => ({ ...provided, maxHeight: "200px", minWidth: "250px" }),
menuList: (provided) => ({ ...provided, minWidth: "250px" }),
option: (provided, state) => ({
...provided,
backgroundColor: state.isSelected
? "#007bff"
: state.isFocused
? "#f8f9fa"
: "white",
color: state.isSelected ? "white" : "black",
}),
}}
placeholder="Select tags..."
/>
<button
className="tick-btn"
style={{ height: "40px" }}
onClick={async () => {
if (!editTag) return;
try {
await saveInlineTags(item.id);
} catch (err) {
console.error("Failed to update tag", err);
}
}}
>
<FaCheck />
</button>
</div>
) : (
<span
style={{
cursor: "pointer",
fontStyle: (!item.tags || item.tags.length === 0) ? "italic" : "normal",
color: (!item.tags || item.tags.length === 0) ? "#888" : "inherit",
}}
onClick={() => openTagEditorForItem(item)}
>
{(!item.tags || item.tags.length === 0)
? "Click to add tags"
: item.tags.map((t) => t.tagName).join(", ")}
</span>
)
) : (
// If current user is not the owner, just display tags (non-editable)
<span
style={{
fontStyle: (!item.tags || item.tags.length === 0) ? "italic" : "normal",
color: (!item.tags || item.tags.length === 0) ? "#888" : "inherit",
}}
>
{(!item.tags || item.tags.length === 0)
? "No tags available"
: item.tags.map((t) => t.tagName).join(", ")}
</span>
)}
</td>

<td>
{item.user_id === Number(localStorage.getItem("userId")) ? (
editingField?.itemId === item.id && editingField.field === "blurb" ? (
<div className="inline-edit-container">
<textarea
className="inline-edit-textarea"
value={editBlurb}
onChange={(e) => setEditBlurb(e.target.value)}
rows={4}
placeholder="Type your notes here..."
style={{
width: "100%",
minWidth: "250px",
resize: "vertical",
padding: "6px",
fontSize: "0.9rem",
}}
/>
<button
className="tick-btn"
onClick={async () => {
try {
await saveInlineBlurb(item.id);
} catch (err) {
console.error("Failed to update blurb", err);
}
}}
>
<FaCheck />
</button>
</div>
) : (
<span
style={{
cursor: "pointer",
fontStyle: !item.blurb ? "italic" : "normal",
color: !item.blurb ? "#888" : "inherit",
}}
onClick={() => {
setEditBlurb(item.blurb || "");
setEditingField({ itemId: item.id, field: "blurb" });
}}
>
{item.blurb || "Click to add notes"}
</span>
)
) : (
// Non-owner view: just show the blurb (read-only)
<span
style={{
fontStyle: !item.blurb ? "italic" : "normal",
color: !item.blurb ? "#888" : "inherit",
}}
>
{item.blurb || "No notes available"}
</span>
)}
</td>

<td>
<a href={item.url} target="_blank" rel="noreferrer">
<FaExternalLinkAlt />
</a>
</td>
<td>{item.source}</td>
<td>{item.addedBy}</td>
<td>{formatDate(item.date)}</td>
</tr>
))
) : (
<tr>
<td colSpan={7}>No items found</td>
</tr>
)}
</tbody>
</table>
{/* Pagination Controls */}
{/* Pagination Controls */}
<div
className="pagination-controls"
style={{
marginTop: "15px",
display: "flex",
justifyContent: "center",
alignItems: "center",
gap: "10px",
flexWrap: "wrap",
}}
>
{/* Showing range */}
<span>
{((page - 1) * pageSize + 1)}–
{Math.min(page * pageSize, totalItems)} of {totalItems} items
</span>

{/* First & Prev */}
<button
className="btn btn-secondary"
disabled={page === 1}
onClick={() => setPage(1)}
>
⏮
</button>
<button
className="btn btn-secondary"
disabled={page === 1}
onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
>
◀
</button>

{/* Numbered Pages */}
{Array.from({ length: Math.ceil(totalItems / pageSize) }, (_, i) => i + 1)
.slice(Math.max(0, page - 3), Math.min(Math.ceil(totalItems / pageSize), page + 2))
.map((p) => (
<button
key={p}
onClick={() => setPage(p)}
style={{
padding: "5px 10px",
borderRadius: "4px",
border: "1px solid #ccc",
background: page === p ? "#007bff" : "#fff",
color: page === p ? "#fff" : "#000",
cursor: "pointer",
}}
>
{p}
</button>
))}

{/* Next & Last */}
<button
className="btn btn-secondary"
disabled={page >= Math.ceil(totalItems / pageSize)}
onClick={() => setPage((prev) => prev + 1)}
>
▶
</button>
<button
className="btn btn-secondary"
disabled={page >= Math.ceil(totalItems / pageSize)}
onClick={() => setPage(Math.ceil(totalItems / pageSize))}
>
⏭
</button>

{/* Page Size Selector */}
<select
value={pageSize}
onChange={(e) => {
setPage(1);
setPageSize(Number(e.target.value));
}}
>
<option value={10}>10</option>
<option value={15}>15</option>
</select>
</div>

</div>
</div>
);
}
